<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <h2 class="title-subsection text-valence-stone-900">New Campaign</h2>
            <p class="body-small text-valence-stone-500">Set up a new assessment campaign for your organization.</p>
        </header>

        <hr class="border-t border-valence-cream-300" />

        <button type="button" class="body-small text-valence-stone-500 hover:text-valence-stone-900 flex items-center gap-1 self-start" @click="emit('back')">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
        </button>

        <BaseCard padding="lg">
            <form class="flex flex-col gap-6" @submit.prevent="submit">
                <label for="campaign-name" class="flex flex-col gap-2">
                    <span class="body-small-bold text-valence-stone-900">Name</span>
                    <input
                        id="campaign-name"
                        v-model="form.name"
                        type="text"
                        required
                        maxlength="255"
                        placeholder="e.g. Q3 2026 leadership assessment"
                        class="w-full px-3 py-2 border border-valence-cream-300 rounded-lgr outline-none focus:border-valence-primary-300 body-small" />
                    <span class="details text-valence-stone-500">Shown to participants on their reflections page.</span>
                </label>

                <hr class="border-t border-valence-cream-300" />

                <fieldset class="flex flex-col gap-2">
                    <legend class="body-small-bold text-valence-stone-900 mb-2">Type</legend>
                    <label for="campaign-type-360" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-360" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="false" class="mt-1" />
                        <span class="flex flex-col gap-0.5">
                            <span class="body-small-bold text-valence-stone-900">360 feedback</span>
                            <span class="body-small text-valence-stone-500">Participants nominate raters and collect anonymous feedback.</span>
                        </span>
                    </label>
                    <label for="campaign-type-self" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-self" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="true" class="mt-1" />
                        <span class="flex flex-col gap-0.5">
                            <span class="body-small-bold text-valence-stone-900">Self reflection only</span>
                            <span class="body-small text-valence-stone-500">No raters — participants rate themselves and get their results right away.</span>
                        </span>
                    </label>
                </fieldset>

                <hr class="border-t border-valence-cream-300" />

                <label for="campaign-template" class="flex flex-col gap-2">
                    <span class="body-small-bold text-valence-stone-900">Template</span>
                    <select
                        id="campaign-template"
                        v-model="form.templateId"
                        required
                        class="w-full px-3 py-2 border border-valence-cream-300 rounded-lgr outline-none focus:border-valence-primary-300 body-small">
                        <option v-for="templateOption in eligibleTemplates" :key="templateOption.id" :value="templateOption.id">{{ templateOption.name }}</option>
                    </select>
                    <span v-if="eligibleTemplates.length === 0" class="details text-red-600">
                        No templates support this campaign type — pick a different type or add a template.
                    </span>
                    <span v-else-if="selectedTemplate" class="details text-valence-stone-500">
                        {{ selectedTemplate.question_count }} question{{ selectedTemplate.question_count === 1 ? "" : "s" }}
                    </span>
                </label>

                <template v-if="!form.isSelfOnly">
                    <hr class="border-t border-valence-cream-300" />
                    <label for="campaign-min-responses" class="flex flex-col gap-2">
                        <span class="body-small-bold text-valence-stone-900">Minimum rater responses</span>
                        <input
                            id="campaign-min-responses"
                            v-model.number="form.minResponses"
                            type="number"
                            min="0"
                            max="32767"
                            required
                            class="w-32 px-3 py-2 border border-valence-cream-300 rounded-lgr outline-none focus:border-valence-primary-300 body-small" />
                        <span class="details text-valence-stone-500">Each participant's results unlock after this many raters respond. 0 unlocks immediately.</span>
                    </label>

                    <hr class="border-t border-valence-cream-300" />

                    <label for="campaign-self-assess" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-self-assess" v-model="form.selfAssessEnabled" type="checkbox" class="mt-1" />
                        <span class="flex flex-col gap-0.5">
                            <span class="body-small-bold text-valence-stone-900">Include self reflection</span>
                            <span class="body-small text-valence-stone-500">Participants also rate themselves, so their report shows self-vs-others gaps.</span>
                        </span>
                    </label>
                </template>

                <hr class="border-t border-valence-cream-300" />

                <label for="campaign-closes-at" class="flex flex-col gap-2">
                    <span class="body-small-bold text-valence-stone-900">Close date <span class="body-small text-valence-stone-500">Optional</span></span>
                    <input
                        id="campaign-closes-at"
                        v-model="form.closesOn"
                        type="date"
                        :min="todayIso"
                        class="w-48 px-3 py-2 border border-valence-cream-300 rounded-lgr outline-none focus:border-valence-primary-300 body-small" />
                    <span class="details text-valence-stone-500">Campaign hard-closes at the end of this day. Leave blank for no deadline.</span>
                </label>

                <BaseNotification v-if="submitError" theme="error" :text="submitError" hide-dismiss />
            </form>
        </BaseCard>

        <div class="flex items-center justify-end gap-3 sticky bottom-4">
            <BaseButton theme="neutral" size="sm" class="base-btn-square" :disabled="submitting" @click="emit('back')">Cancel</BaseButton>
            <!-- LAB-988 dark CTA override — see .base-btn-dark in tailwind.css. -->
            <BaseButton theme="primary" size="sm" class="base-btn-dark" :disabled="submitting || !canSubmit" @click="submit">{{
                submitting ? "Creating…" : "Create Campaign"
            }}</BaseButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, TemplateSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { computed, ref, watch } from "vue";

interface Props {
    templates: TemplateSummary[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (event: "back"): void;
    (event: "created", campaign: AdminCampaignSummary): void;
}>();

const submitting = ref(false);
const submitError = ref<string | null>(null);

const firstDefaultTemplate = props.templates.find((t) => t.assessment_mode !== "self_only") ?? props.templates[0] ?? null;
const form = ref({
    name: "",
    isSelfOnly: false,
    templateId: firstDefaultTemplate?.id ?? null,
    minResponses: firstDefaultTemplate?.min_responses ?? 0,
    selfAssessEnabled: true,
    closesOn: "",
});

const todayIso = new Date().toISOString().slice(0, 10);
const eligibleTemplates = computed<TemplateSummary[]>(() =>
    props.templates.filter((t) => (form.value.isSelfOnly ? t.assessment_mode !== "rater_360" : t.assessment_mode !== "self_only")),
);
const selectedTemplate = computed<TemplateSummary | null>(() => eligibleTemplates.value.find((t) => t.id === form.value.templateId) ?? null);
const canSubmit = computed(() => form.value.name.trim().length > 0 && selectedTemplate.value !== null && form.value.minResponses >= 0);

watch(eligibleTemplates, (eligible) => {
    if (!eligible.some((t) => t.id === form.value.templateId)) {
        form.value.templateId = eligible[0]?.id ?? null;
    }
});

watch(selectedTemplate, (template) => {
    if (template) {
        form.value.minResponses = template.min_responses;
    }
});

async function submit() {
    if (!canSubmit.value) {
        return;
    }
    submitting.value = true;
    submitError.value = null;
    try {
        const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns")
            .post(
                JSON.stringify({
                    name: form.value.name.trim(),
                    template_id: form.value.templateId,
                    is_self_assessment_only: form.value.isSelfOnly,
                    min_responses: form.value.isSelfOnly ? 0 : form.value.minResponses,
                    self_assess_enabled: form.value.isSelfOnly ? true : form.value.selfAssessEnabled,
                    closes_at: form.value.closesOn ? new Date(`${form.value.closesOn}T23:59:59`).toISOString() : null,
                }),
                "application/json",
            )
            .json<AdminCampaignResponse>();
        if (fetchError.value) {
            submitError.value = (data.value as { error?: string } | null)?.error || "Could not create the campaign. Please try again.";
            return;
        }
        if (data.value) {
            emit("created", data.value.campaign);
        }
    } finally {
        submitting.value = false;
    }
}
</script>
